import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  MessageCircle, X, Send, Loader2, Bot, User, ExternalLink,
  PhoneCall, GraduationCap, Users, Search, ArrowRight,
} from "lucide-react";
import { WHATSAPP } from "@/lib/site";
import { streamChatMessage, checkApplication } from "@/lib/chatbotApi";
import { toast } from "sonner";

// -- Session id (persisted per browser) --
function getSessionId() {
  try {
    let sid = localStorage.getItem("asai-chat-sid");
    if (!sid) {
      sid = "cs_" + Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
      localStorage.setItem("asai-chat-sid", sid);
    }
    return sid;
  } catch {
    return "cs_ephemeral";
  }
}

// -- Client-side quick-action metadata (icons + navigate targets) --
const ACTION_META = {
  view_programs:     { label: "View Programs",     icon: GraduationCap, to: "/courses" },
  apply_now:         { label: "Apply Now",         icon: ArrowRight,    to: "/apply" },
  request_callback:  { label: "Request Callback",  icon: PhoneCall,     form: "callback" },
  whatsapp:          { label: "Chat on WhatsApp",  icon: ExternalLink,  href: WHATSAPP.link, external: true },
  invite_expert:     { label: "Invite an Expert",  icon: Users,         to: "/expert-sessions" },
  check_application: { label: "Check Application", icon: Search,        form: "check_application" },
};

const WELCOME = {
  role: "bot",
  reply: "Hi! I'm the ASAI Assistant. I can share information about our programs, fees, application process, address, career support and more. What would you like to know?",
  suggestedActions: ["view_programs", "apply_now", "check_application", "whatsapp"],
};

export const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [activeForm, setActiveForm] = useState(null); // "callback" | "check_application" | null
  const scrollRef = useRef(null);
  const nav = useNavigate();
  const sid = useRef(getSessionId());

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, activeForm]);

  const push = (m) => setMessages((cur) => [...cur, m]);
  const patchLast = (fn) => setMessages((cur) => {
    const next = cur.slice();
    next[next.length - 1] = fn(next[next.length - 1]);
    return next;
  });

  const onSend = async (msg) => {
    const q = (msg ?? input).trim();
    if (!q || sending) return;
    setInput("");
    push({ role: "user", reply: q });
    setSending(true);
    let started = false;
    try {
      await streamChatMessage(q, sid.current, (ev) => {
        if (!started) { push({ role: "bot", reply: "", suggestedActions: [], streaming: true }); started = true; }
        if (ev.type === "delta") patchLast((m) => ({ ...m, reply: m.reply + ev.content }));
        else if (ev.type === "replace") patchLast((m) => ({ ...m, reply: ev.content }));
        else if (ev.type === "done") patchLast((m) => ({ ...m, suggestedActions: ev.suggestedActions || [], source: ev.source, streaming: false }));
      });
      if (!started) throw new Error("empty stream");
    } catch {
      const errMsg = {
        role: "bot",
        reply: "I couldn't reach the ASAI server just now. Please try again in a moment, or tap WhatsApp to reach us.",
        suggestedActions: ["whatsapp"],
      };
      if (started) patchLast(() => errMsg); else push(errMsg);
    } finally {
      setSending(false);
    }
  };

  const handleAction = (key) => {
    const meta = ACTION_META[key];
    if (!meta) return;
    if (meta.form) {
      setActiveForm(meta.form);
      return;
    }
    if (meta.external && meta.href) {
      window.open(meta.href, "_blank", "noopener");
      return;
    }
    if (meta.to) {
      nav(meta.to);
      setOpen(false);
    }
  };

  const suggestedPrompts = [
    "What programs do you offer?",
    "What is the fee?",
    "How do I apply?",
    "What is your address?",
    "Do you conduct expert sessions?",
  ];

  return (
    <>
      {/* Floating trigger */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="chatbot-toggle"
        aria-label={open ? "Close ASAI assistant" : "Open ASAI assistant"}
        className="fixed bottom-24 right-6 z-50 inline-flex h-14 w-14 items-center justify-center rounded-full bg-royal text-white shadow-[0_10px_30px_-6px_rgba(29,78,216,0.6)] transition-transform duration-300 hover:scale-110"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            data-testid="chatbot-panel"
            className="fixed bottom-40 right-6 z-50 flex h-[min(600px,calc(100vh-180px))] w-[min(400px,calc(100vw-32px))] flex-col overflow-hidden rounded-2xl border border-navy/10 bg-white shadow-[0_30px_60px_-20px_rgba(6,15,34,0.5)]"
          >
            {/* Header */}
            <div className="flex items-center justify-between bg-navy px-5 py-4 text-white">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-orange/20 text-orange">
                  <Bot className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-sm font-semibold">ASAI Assistant</div>
                  <div className="text-[11px] text-white/60">AI-powered · grounded in approved ASAI content</div>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)} data-testid="chatbot-close"
                className="rounded-full p-1 text-white/70 hover:bg-white/10 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto bg-lightgrey px-4 py-4" data-testid="chatbot-messages">
              {messages.map((m, i) => (
                <MessageBubble key={i} msg={m} onAction={handleAction} />
              ))}
              {sending && !messages[messages.length - 1]?.streaming && (
                <div className="flex items-center gap-2 text-xs text-navy/60" data-testid="chatbot-typing">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-orange" /> ASAI is typing…
                </div>
              )}
            </div>

            {/* Suggested chips (only visible when just the welcome msg exists) */}
            {messages.length === 1 && !activeForm && (
              <div className="border-t border-navy/10 bg-white px-4 py-3">
                <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-navy/50">
                  Try asking
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {suggestedPrompts.map((p) => (
                    <button
                      key={p} onClick={() => onSend(p)} data-testid={`chatbot-prompt-${p.slice(0,10)}`}
                      className="rounded-full border border-navy/15 bg-white px-3 py-1 text-xs text-navy/70 hover:border-orange hover:text-orange"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Inline form or composer */}
            {activeForm === "check_application" ? (
              <CheckApplicationForm
                onCancel={() => setActiveForm(null)}
                onResult={(text) => { push({ role: "bot", reply: text, suggestedActions: ["whatsapp", "request_callback"] }); setActiveForm(null); }}
              />
            ) : activeForm === "callback" ? (
              <CallbackForm
                onCancel={() => setActiveForm(null)}
                onSubmit={() => { push({ role: "bot", reply: "Thanks! Our team will call you shortly on the number you provided.", suggestedActions: ["whatsapp"] }); setActiveForm(null); }}
              />
            ) : (
              <form
                onSubmit={(e) => { e.preventDefault(); onSend(); }}
                className="flex items-center gap-2 border-t border-navy/10 bg-white px-3 py-3"
              >
                <input
                  type="text" value={input} onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about programs, fees, application…" data-testid="chatbot-input"
                  disabled={sending}
                  className="flex-1 rounded-full border border-navy/15 bg-white px-4 py-2 text-sm outline-none focus:border-royal disabled:opacity-60"
                />
                <button
                  type="submit" disabled={sending || !input.trim()} data-testid="chatbot-send"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-orange text-white transition-transform hover:scale-105 disabled:opacity-50"
                >
                  <Send className="h-4 w-4" />
                </button>
              </form>
            )}

            <div className="border-t border-navy/10 bg-white px-4 py-2 text-[10px] text-navy/45">
              Answers are from approved ASAI content. For personal / case-specific queries, please contact our team.
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ---------- Sub-components ----------

const URL_RE = /(https?:\/\/[^\s)]+)/g;
const isUrl = (p) => /^https?:\/\//.test(p);

const Linkified = ({ text }) =>
  text.split(URL_RE).map((part, i) =>
    isUrl(part) ? (
      <a
        key={i} href={part} target="_blank" rel="noopener noreferrer"
        data-testid="chatbot-link"
        className="font-medium text-royal underline underline-offset-2 hover:text-orange"
      >
        {part.replace(/^https?:\/\//, "").replace(/\/$/, "")}
      </a>
    ) : (
      <span key={i}>{part}</span>
    )
  );

const MessageBubble = ({ msg, onAction }) => {
  const isUser = msg.role === "user";
  return (
    <div className={`flex gap-2 ${isUser ? "justify-end" : "justify-start"}`} data-testid={`chatbot-msg-${isUser ? "user" : "bot"}`}>
      {!isUser && (
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-orange/15 text-orange">
          <Bot className="h-3.5 w-3.5" />
        </span>
      )}
      <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${isUser ? "bg-navy text-white" : "bg-white text-navy/85 shadow-sm"}`}>
        <div className="whitespace-pre-wrap">
          {isUser ? msg.reply : <Linkified text={msg.reply} />}
          {msg.streaming && <span className="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse rounded-sm bg-orange align-middle" />}
        </div>
        {!isUser && msg.suggestedActions?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {msg.suggestedActions.map((k) => {
              const meta = ACTION_META[k];
              if (!meta) return null;
              const Icon = meta.icon;
              return (
                <button
                  key={k} onClick={() => onAction(k)} data-testid={`chatbot-action-${k}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-navy/15 bg-lightgrey px-3 py-1 text-xs font-medium text-navy hover:border-orange hover:text-orange"
                >
                  <Icon className="h-3.5 w-3.5" /> {meta.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {isUser && (
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-navy text-white">
          <User className="h-3.5 w-3.5" />
        </span>
      )}
    </div>
  );
};

const CheckApplicationForm = ({ onCancel, onResult }) => {
  const [reference, setReference] = useState("");
  const [verifier, setVerifier] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!reference.trim() || !verifier.trim()) {
      toast.error("Please enter both fields.");
      return;
    }
    setBusy(true);
    try {
      const res = await checkApplication(reference.trim().toUpperCase(), verifier.trim());
      const a = res.application;
      const text = (
        `Application found ✅\n` +
        `• Reference: ${a.referenceNumber}\n` +
        `• Name: ${a.fullName}\n` +
        `• Mobile: ${a.mobile}\n` +
        `• Program: ${a.programInterested || "—"}\n` +
        `• Mode: ${a.preferredMode || "—"}\n` +
        `• Status: ${a.status}\n` +
        `• Submitted: ${(a.createdAt || "").slice(0, 10)}\n` +
        `Note: Program selection is subject to review; admission is confirmed only after formal communication from ASAI.`
      );
      onResult(text);
    } catch (err) {
      toast.error(err?.message || "We couldn't verify your application.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="border-t border-navy/10 bg-white px-4 py-3" data-testid="chatbot-check-form">
      <div className="mb-2 text-xs font-semibold text-navy">Check application status</div>
      <input
        value={reference} onChange={(e) => setReference(e.target.value)}
        placeholder="Reference (ASAI-YYMMDD-XXXXXX)"
        data-testid="chatbot-check-reference"
        className="mb-2 w-full rounded-lg border border-navy/15 px-3 py-2 text-sm focus:border-royal focus:outline-none"
      />
      <input
        value={verifier} onChange={(e) => setVerifier(e.target.value)}
        placeholder="Registered email or last 4 digits of mobile"
        data-testid="chatbot-check-verifier"
        className="mb-3 w-full rounded-lg border border-navy/15 px-3 py-2 text-sm focus:border-royal focus:outline-none"
      />
      <div className="flex items-center justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-full border border-navy/15 px-4 py-1.5 text-xs text-navy/70 hover:border-navy" data-testid="chatbot-check-cancel">
          Cancel
        </button>
        <button type="submit" disabled={busy} data-testid="chatbot-check-submit"
          className="inline-flex items-center gap-1 rounded-full bg-orange px-4 py-1.5 text-xs font-medium text-white hover:bg-orange/90 disabled:opacity-60">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />} Check
        </button>
      </div>
    </form>
  );
};

const CallbackForm = ({ onCancel, onSubmit }) => {
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim() || !/^[6-9]\d{9}$/.test(mobile.replace(/\D/g, ""))) {
          toast.error("Please enter your name and a valid 10-digit mobile number.");
          return;
        }
        // Route to WhatsApp with a pre-filled message (avoids duplicating enquiry API for a lightweight callback ask)
        const text = `Hi ASAI team, this is ${name}. Please call me back on ${mobile}.`;
        window.open(`https://wa.me/${WHATSAPP.number}?text=${encodeURIComponent(text)}`, "_blank", "noopener");
        onSubmit();
      }}
      className="border-t border-navy/10 bg-white px-4 py-3"
      data-testid="chatbot-callback-form"
    >
      <div className="mb-2 text-xs font-semibold text-navy">Request a callback</div>
      <input
        value={name} onChange={(e) => setName(e.target.value)}
        placeholder="Your name" data-testid="chatbot-callback-name"
        className="mb-2 w-full rounded-lg border border-navy/15 px-3 py-2 text-sm focus:border-royal focus:outline-none"
      />
      <input
        value={mobile} onChange={(e) => setMobile(e.target.value)}
        placeholder="10-digit mobile" data-testid="chatbot-callback-mobile"
        className="mb-3 w-full rounded-lg border border-navy/15 px-3 py-2 text-sm focus:border-royal focus:outline-none"
      />
      <div className="flex items-center justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-full border border-navy/15 px-4 py-1.5 text-xs text-navy/70 hover:border-navy" data-testid="chatbot-callback-cancel">
          Cancel
        </button>
        <button type="submit" data-testid="chatbot-callback-submit"
          className="inline-flex items-center gap-1 rounded-full bg-orange px-4 py-1.5 text-xs font-medium text-white hover:bg-orange/90">
          <PhoneCall className="h-3 w-3" /> Send on WhatsApp
        </button>
      </div>
    </form>
  );
};
